import {
  useUpdateSuspensionRequest,
  type SuspensionRequest,
} from "@/queries/deadline-suspensions/deadline-suspensions.api";
import type { ApprovalGroupType } from "@/types/entities/approval-groups";
import { useToast } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { ApproveSuspensionInfo } from "./ApproveInfoModal";
import ApproveInputModal from "./ApproveInputModal";
import { STEPS } from "@/pages/extension/modal/types";
import DenyModal from "@/pages/extension/modal/DenyModal";
import SendToLeadModal from "@/pages/extension/modal/SendToLeadModal";

interface Props {
  isOpen: boolean;
  canSendToLead?: boolean;
  onClose: () => void;
  group?: ApprovalGroupType;
  data: SuspensionRequest;
  canAction?: boolean;
  max_days_allowed?: number;
}

const ApproveSuspensionModal = ({
  isOpen,
  canSendToLead,
  onClose,
  group,
  data,
  canAction,
  max_days_allowed,
}: Props) => {
  const { showToast } = useToast();
  const { mutateAsync: updateRequest, isPending } = useUpdateSuspensionRequest(
    data.id,
  );
  const [step, setStep] = useState<STEPS>(STEPS.MAIN);

  const onApprove = async (days: string) => {
    try {
      const daysGranted = parseInt(days, 10);
      if (isNaN(daysGranted)) {
        throw new Error("Días inválidos");
      }
      await updateRequest({
        action: "APPROVE",
        approved_days: daysGranted,
      });
      showToast({
        title: "Extensión de plazo aprobada",
        description: "La extensión ha sido aprobada correctamente.",
        options: {
          type: "success",
        },
      });
      onClose();
    } catch {
      showToast({
        title: "Error al aprobar la extensión",
        description:
          "Ha ocurrido un error al aprobar la extensión. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  const onDeny = async (reason: string) => {
    try {
      await updateRequest({ action: "DENY", observation: reason });
      onClose();
    } catch {
      showToast({
        title: "Error al denegar la extensión",
        description:
          "Ha ocurrido un error al denegar la extensión. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  const onSendToLead = async (reason: string) => {
    const leader = group?.members.find((member) => {
      return member.seniority === 1;
    });
    try {
      await updateRequest({
        action: "SCALE",
        observation: reason,
        scaled_to: leader?.id,
      });
      onClose();
    } catch {
      showToast({
        title: "Error al enviar al líder",
        description:
          "Ha ocurrido un error al enviar la extensión al líder. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  return (
    <>
      <ApproveSuspensionInfo
        isOpen={isOpen && step === STEPS.MAIN}
        onClose={onClose}
        onApprove={() => setStep(STEPS.APPROVE)}
        onDenied={() => setStep(STEPS.DENY)}
        onSendToLead={() => setStep(STEPS.SEND_TO_LEAD)}
        data={data}
        canSendToLead={canSendToLead}
        canAction={canAction}
      />
      <ApproveInputModal
        maxDaysAllowed={max_days_allowed}
        isOpen={isOpen && step === STEPS.APPROVE}
        onClose={() => setStep(STEPS.MAIN)}
        onApprove={onApprove}
        acceptButtonDetails={{
          label: "Aprobar",
          variant: undefined,
        }}
        isLoading={isPending}
      />
      <DenyModal
        isExtension={false}
        isOpen={isOpen && step === STEPS.DENY}
        onClose={() => setStep(STEPS.MAIN)}
        onDeny={onDeny}
        acceptButtonDetails={{
          label: "Denegar",
          variant: "danger-primary",
        }}
        isLoading={isPending}
      />
      <SendToLeadModal
        isOpen={isOpen && step === STEPS.SEND_TO_LEAD}
        onClose={() => setStep(STEPS.MAIN)}
        onSendToLead={onSendToLead}
        acceptButtonDetails={{
          label: "Enviar al líder",
          variant: undefined,
        }}
        isLoading={isPending}
      />
    </>
  );
};

export default ApproveSuspensionModal;
